// useNudges: smart nudge framework.
//
// Decides which in-app nudge (if any) to show to the user right now, based on
// recent activity in the user_nudges table plus live counts from other modules
// (mirror_context, daily_checkins, sleep_logs, goals).
//
// The user_nudges table is cast via (supabase as any) because the generated
// types.ts does not yet include it — same pattern used by useBrain, useXP,
// and useAvatarSnapshot elsewhere in the codebase.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { checkInDayWindow } from "@/lib/checkInDates";

export type NudgeType =
  | "teach_mirror_incomplete"
  | "checkin_missed_today"
  | "sleep_not_logged_3days"
  | "goals_empty";

export type NudgeVariant = "info" | "warning" | "gentle";

export interface Nudge {
  id?: string;
  type: NudgeType;
  title: string;
  description: string;
  ctaLabel: string;
  ctaAction: () => void;
  variant: NudgeVariant;
  priority: number;
}

interface UserNudgeRow {
  id: string;
  user_id: string;
  nudge_type: NudgeType;
  shown_at: string | null;
  dismissed_at: string | null;
  acted_on_at: string | null;
  cooldown_until: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const COOLDOWN_DAYS: Record<NudgeType, number> = {
  teach_mirror_incomplete: 7,
  checkin_missed_today: 1,
  sleep_not_logged_3days: 3,
  goals_empty: 14,
};

// 3 consecutive dismissals of the same nudge_type disable that category for
// STRIKE_DISABLE_DAYS. Consecutive = no acted_on_at between them.
const STRIKE_THRESHOLD = 3;
const STRIKE_DISABLE_DAYS = 30;

const MS_PER_DAY = 86400000;

function todayDate(): string {
  return new Date().toISOString().split("T")[0];
}

function daysAgoDate(n: number): string {
  return new Date(Date.now() - n * MS_PER_DAY).toISOString().split("T")[0];
}

function addDaysIso(days: number): string {
  return new Date(Date.now() + days * MS_PER_DAY).toISOString();
}

// Walk a type's row history (most recent first) counting dismissals that
// happened after the last acted_on_at (or forever if never acted). Returns
// both the count and the most recent dismissal timestamp.
function countConsecutiveDismissals(rows: UserNudgeRow[]): {
  count: number;
  latestDismissedAt: Date | null;
} {
  let count = 0;
  let latestDismissedAt: Date | null = null;
  for (const r of rows) {
    if (r.acted_on_at) break;
    if (r.dismissed_at) {
      count += 1;
      const d = new Date(r.dismissed_at);
      if (!latestDismissedAt || d > latestDismissedAt) latestDismissedAt = d;
    }
  }
  return { count, latestDismissedAt };
}

export const useNudges = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeNudge, setActiveNudge] = useState<Nudge | null>(null);
  const [loading, setLoading] = useState(true);

  // Guards against double-inserting the same shown_at row on a single mount.
  // Keeps the id once inserted so dismiss/act can update the correct row.
  const insertedIdRef = useRef<string | null>(null);
  const resolvedOnceRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const evaluate = async () => {
      try {
        // Use getSession() instead of getUser() to avoid navigator.locks
        // contention. getUser() validates with the server and acquires the
        // auth lock; getSession() reads localStorage directly. For nudge
        // gating we only need user.id; downstream RLS handles stale sessions.
        // Sentry FRONTEND-1/2/4 (lock stolen by another request) — fix 02/05.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) {
          if (!cancelled) setLoading(false);
          return;
        }

        // Pull this user's recent nudge history (last 60 days, most recent first).
        const sixtyDaysAgoIso = new Date(Date.now() - 60 * MS_PER_DAY).toISOString();
        const { data: nudgeRows, error: nudgeErr } = await (supabase as any)
          .from("user_nudges")
          .select(
            "id, user_id, nudge_type, shown_at, dismissed_at, acted_on_at, cooldown_until, metadata, created_at"
          )
          .eq("user_id", user.id)
          .gte("created_at", sixtyDaysAgoIso)
          .order("created_at", { ascending: false });

        if (cancelled) return;
        if (nudgeErr) {
          console.error("[useNudges] history load failed:", nudgeErr);
          if (!cancelled) setLoading(false);
          return;
        }

        const history = (nudgeRows as UserNudgeRow[]) || [];
        const now = new Date();

        // Pre-compute cooldown + strike gating per type so we can cheaply
        // decide which content-queries to skip.
        const gating = new Map<NudgeType, { skip: boolean }>();
        const allTypes: NudgeType[] = [
          "teach_mirror_incomplete",
          "checkin_missed_today",
          "sleep_not_logged_3days",
          "goals_empty",
        ];
        for (const type of allTypes) {
          const rowsOfType = history.filter((r) => r.nudge_type === type);
          const latest = rowsOfType[0];
          if (latest?.cooldown_until && new Date(latest.cooldown_until) > now) {
            gating.set(type, { skip: true });
            continue;
          }
          const strike = countConsecutiveDismissals(rowsOfType);
          if (
            strike.count >= STRIKE_THRESHOLD &&
            strike.latestDismissedAt &&
            now.getTime() - strike.latestDismissedAt.getTime() <=
              STRIKE_DISABLE_DAYS * MS_PER_DAY
          ) {
            gating.set(type, { skip: true });
            continue;
          }
          gating.set(type, { skip: false });
        }

        // Eligibility queries (each independent; failures produce null → skip).
        const runQuery = async <T>(
          label: string,
          fn: () => Promise<{ count: number | null; error: unknown }>
        ): Promise<number | null> => {
          try {
            const { count, error } = await fn();
            if (error) {
              console.error("[useNudges] query failed", { label, error });
              return null;
            }
            return count ?? 0;
          } catch (err) {
            console.error("[useNudges] query threw", { label, error: err });
            return null;
          }
        };

        const checkinDay = checkInDayWindow(now);
        const [mirrorContextCount, checkinTodayCount, sleep3dCount, openGoalsCount] =
          await Promise.all([
            gating.get("teach_mirror_incomplete")!.skip
              ? Promise.resolve(null)
              : runQuery("mirror_context", () =>
                  (supabase as any)
                    .from("mirror_context")
                    .select("id", { count: "exact", head: true })
                    .eq("user_id", user.id)
                    .neq("content", "")
                ),
            gating.get("checkin_missed_today")!.skip
              ? Promise.resolve(null)
              : runQuery("checkin_today", () =>
                  (supabase as any)
                    .from("daily_checkins")
                    .select("id", { count: "exact", head: true })
                    .eq("user_id", user.id)
                    .gte("checked_in_at", checkinDay.start)
                    .lt("checked_in_at", checkinDay.end)
                    .lte("checked_in_at", now.toISOString())
                ),
            gating.get("sleep_not_logged_3days")!.skip
              ? Promise.resolve(null)
              : runQuery("sleep_3d", () =>
                  (supabase as any)
                    .from("sleep_logs")
                    .select("id", { count: "exact", head: true })
                    .eq("user_id", user.id)
                    .gte("sleep_date", daysAgoDate(3))
                ),
            gating.get("goals_empty")!.skip
              ? Promise.resolve(null)
              : runQuery("open_goals", () =>
                  (supabase as any)
                    .from("goals")
                    .select("id", { count: "exact", head: true })
                    .eq("user_id", user.id)
                    .eq("is_completed", false)
                ),
          ]);

        if (cancelled) return;

        const eligible: Nudge[] = [];

        // teach_mirror_incomplete — user has fewer than 3 filled context rows
        if (
          !gating.get("teach_mirror_incomplete")!.skip &&
          mirrorContextCount != null &&
          mirrorContextCount < 3
        ) {
          eligible.push({
            type: "teach_mirror_incomplete",
            title: t("nudges.teach_mirror_incomplete.title"),
            description: t("nudges.teach_mirror_incomplete.description"),
            ctaLabel: t("nudges.teach_mirror_incomplete.cta"),
            ctaAction: () => navigate("/teach-mirror"),
            variant: "info",
            priority: 100,
          });
        }

        // checkin_missed_today — no check-in today AND it's after 18:00 local
        const afterSix = new Date().getHours() >= 18;
        if (
          !gating.get("checkin_missed_today")!.skip &&
          checkinTodayCount === 0 &&
          afterSix
        ) {
          eligible.push({
            type: "checkin_missed_today",
            title: t("nudges.checkin_missed_today.title"),
            description: t("nudges.checkin_missed_today.description"),
            ctaLabel: t("nudges.checkin_missed_today.cta"),
            ctaAction: () => navigate("/check-in"),
            variant: "gentle",
            priority: 80,
          });
        }

        // sleep_not_logged_3days — zero sleep_logs in last 3 days
        if (
          !gating.get("sleep_not_logged_3days")!.skip &&
          sleep3dCount === 0
        ) {
          eligible.push({
            type: "sleep_not_logged_3days",
            title: t("nudges.sleep_not_logged_3days.title"),
            description: t("nudges.sleep_not_logged_3days.description"),
            ctaLabel: t("nudges.sleep_not_logged_3days.cta"),
            ctaAction: () => navigate("/sleep"),
            variant: "gentle",
            priority: 60,
          });
        }

        // goals_empty — no open goals
        if (!gating.get("goals_empty")!.skip && openGoalsCount === 0) {
          eligible.push({
            type: "goals_empty",
            title: t("nudges.goals_empty.title"),
            description: t("nudges.goals_empty.description"),
            ctaLabel: t("nudges.goals_empty.cta"),
            ctaAction: () => navigate("/goals"),
            variant: "info",
            priority: 40,
          });
        }

        // Highest priority wins; ties broken by insertion order.
        eligible.sort((a, b) => b.priority - a.priority);
        const chosen = eligible[0] ?? null;

        if (!chosen) {
          if (!cancelled) {
            setActiveNudge(null);
            setLoading(false);
          }
          return;
        }

        // First resolution this mount — record shown_at so dismissal/action
        // updates have a row to target.
        if (!resolvedOnceRef.current) {
          resolvedOnceRef.current = true;
          try {
            const { data: inserted, error: insErr } = await (supabase as any)
              .from("user_nudges")
              .insert({
                user_id: user.id,
                nudge_type: chosen.type,
                shown_at: new Date().toISOString(),
              })
              .select("id")
              .single();
            if (insErr) {
              console.error("[useNudges] insert shown_at failed:", insErr);
            } else if (inserted?.id) {
              insertedIdRef.current = inserted.id as string;
              chosen.id = inserted.id as string;
            }
          } catch (err) {
            console.error("[useNudges] insert threw:", err);
          }
        }

        if (!cancelled) {
          setActiveNudge(chosen);
          setLoading(false);
        }
      } catch (err) {
        console.error("[useNudges] evaluate threw:", err);
        if (!cancelled) setLoading(false);
      }
    };

    evaluate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissNudge = async () => {
    const rowId = insertedIdRef.current;
    const current = activeNudge;
    setActiveNudge(null);
    if (!rowId || !current) return;
    const cooldown = addDaysIso(COOLDOWN_DAYS[current.type]);
    try {
      const { error } = await (supabase as any)
        .from("user_nudges")
        .update({
          dismissed_at: new Date().toISOString(),
          cooldown_until: cooldown,
        })
        .eq("id", rowId);
      if (error) console.error("[useNudges] dismiss update failed:", error);
    } catch (err) {
      console.error("[useNudges] dismiss threw:", err);
    }
  };

  const actOnNudge = async () => {
    const rowId = insertedIdRef.current;
    const current = activeNudge;
    setActiveNudge(null);
    if (!current) return;
    // Fire navigation/action immediately; DB write is fire-and-forget so the
    // CTA feels instant even if the network is slow.
    try {
      current.ctaAction();
    } catch (err) {
      console.error("[useNudges] ctaAction threw:", err);
    }
    if (!rowId) return;
    const cooldown = addDaysIso(COOLDOWN_DAYS[current.type]);
    try {
      const { error } = await (supabase as any)
        .from("user_nudges")
        .update({
          acted_on_at: new Date().toISOString(),
          cooldown_until: cooldown,
        })
        .eq("id", rowId);
      if (error) console.error("[useNudges] act update failed:", error);
    } catch (err) {
      console.error("[useNudges] act threw:", err);
    }
  };

  return { activeNudge, dismissNudge, actOnNudge, loading };
};
